/**
 * @file Set of color functions
 * @author Heino Lenting
 */
var ColorTools = /** @class */ (function () {
    /**
     * Creates an instance of ColorTools.
     */
    function ColorTools() {
        /**
         * The color is used instead of white
         */
        this._defaultColor = '#C0C0C0'; // silver
    }
    Object.defineProperty(ColorTools.prototype, "defaultColor", {
        /**
         * Gets the default display color
         * @readonly
         * @type {string}
         */
        get: function () {
            return this._defaultColor;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a darker or lighter variant of the color, see: Based on https://www.sitepoint.com/javascript-generate-lighter-darker-color/
     * @param color The color
     * @param luminance The luminance (range: -1 and 1)
     * @returns The calculated color
     */
    ColorTools.prototype.colorLuminance = function (color, luminance) {
        if (luminance === void 0) { luminance = 0; }
        // Get rid of #
        if (color.length > 6) {
            color = color.substr(1);
        }
        // Convert to decimal and change luminosity
        var colorValueNew = '#', c, i;
        for (i = 0; i < 3; i++) {
            c = parseInt(color.substr(i * 2, 2), 16);
            c = Math.round(Math.min(Math.max(0, c + (c * luminance)), 255)).toString(16);
            colorValueNew += ('00' + c).substr(c.length);
        }
        return colorValueNew;
    };
    /**
     * Returns a darker variant of the color
     * @param color The color
     * @param percentage The percentage
     * @returns The darker color
     */
    ColorTools.prototype.darken = function (color, percentage) {
        if (percentage === void 0) { percentage = 20; }
        return this.colorLuminance(color, (percentage / 100) * -1);
    };
    /**
     * Returns a lighter variant of the color
     * @param color The color
     * @param percentage The percentage
     * @returns The lighter color
     */
    ColorTools.prototype.lighten = function (color, percentage) {
        if (percentage === void 0) { percentage = 20; }
        return this.colorLuminance(color, (percentage / 100));
    };
    /**
     * Returns the contrasting color (darken or ligten) w.r.t. the color
     * @param color The color for which the highest contrast is returned
     * @param percentage The percentage
     * @returns The highest contrasting color with the parameter
     */
    ColorTools.prototype.maxContrast = function (color, percentage) {
        if (percentage === void 0) { percentage = 20; }
        return this.isDarkenMaxContrast(color) ? this.darken(color, percentage) : this.lighten(color, percentage);
    };
    /**
     * Returns true if the highest contrasting color is darker
     * @param color The color
     * @returns True if highest contrasting color is darker
     */
    ColorTools.prototype.isDarkenMaxContrast = function (color) {
        if (!color) {
            return true;
        }
        var r = parseInt(color.substr(1, 2), 16);
        var g = parseInt(color.substr(3, 2), 16);
        var b = parseInt(color.substr(5, 2), 16);
        return (299 * r + 587 * g + 114 * b > 127000);
    };
    /**
     * Returns the grayscale variant of given color.
     * @param color The color
     * @returns The grayed variant of the color
     */
    ColorTools.prototype.grayscale = function (color) {
        var r = parseInt(color.substr(1, 2), 16);
        var g = parseInt(color.substr(3, 2), 16);
        var b = parseInt(color.substr(5, 2), 16);
        var part = Math.round(r * 0.3 + g * .59 + b * .11).toString(16);
        var grayscale = '#' + part + part + part;
        return grayscale;
    };
    /**
     * Returns the highest contrasting color (black or white)
     * @param color The color for which the highest contrast is returned
     * @returns The highest contrasting color with the parameter
     */
    ColorTools.prototype.contrast = function (color) {
        return this.isDarkenMaxContrast(color) ? '#222' : '#ddd';
    };
    /**
     * Returns the same color, or default color if it is white
     * @param {string} color The actual color
     * @returns {string} The color to be displayed
     */
    ColorTools.prototype.getDisplayColor = function (color) {
        return color === '#FFFFFF' ? this._defaultColor : color;
    };
    return ColorTools;
}());
export { ColorTools };
